import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Row, Spinner } from "reactstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiArrowLeft } from "@mdi/js";
import { toast } from "react-toastify";
import BaseButton from "../../Components/Common/BaseButton";
import BaseInput from "../../Components/Common/BaseInput";
import BaseSelectDropdown from "../../Components/Common/BaseSelectDropdown";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  validationMessages,
  InputPlaceHolder,
} from "Components/constants/validation";
import {
  REQUEST_ADD_MEMBERS_COMMON,
  REQUEST_UPDATE_MEMBERS_COMMON,
  REQUEST_FILE_UPLOAD_COMMON,
  REQUEST_VIEW_MEMBERS_COMMON,
} from "API/Services/common";
import {
  checkStatusCodeSuccess,
  MembersPosition,
} from "Components/constants/enums";
import { BaseImageURL } from "API/Services/services";
import {
  ADD_MEMBER,
  BACK,
  EDIT_MEMBER,
  IMAGE_ONE_MB_TOAST,
  MEMBER_IMAGE,
  MEMBER_NAME,
  MEMBER_POSITION,
  MEMBERS_TITLE,
  ONLY_IMAGES_ALLOWED_TOAST,
  SUBMIT_LABEL,
  SUBMITTING_LABEL,
  UPDATE_LABEL,
  UPDATING_LABEL,
} from "Components/constants/commonNames";
import "../../STYLE/BaseSelectDropdown.css";
const MembersPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [fullScreenLoader, setFullScreenLoader] = useState(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [viewMemberData, setviewMemberData] = useState<any>();
  const MemberId = id ? Number(id) : undefined;

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(validationMessages.required(MEMBER_NAME)),
    profile_image: Yup.mixed().required(
      validationMessages.required(MEMBER_IMAGE)
    ),
    position: Yup.string().required(
      validationMessages.required(MEMBER_POSITION)
    ),
  });
  const handleBlur = (e: React.FocusEvent<any>) => {
    formik.handleBlur(e); 
  };
  const formik = useFormik({
    validationSchema,
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true,
    initialValues: {
      name: id ? viewMemberData?.name || "" : "",
      profile_image: id ? String(viewMemberData?.profile_image) : "",
      position: id ? String(viewMemberData?.position) : "",
    },
    onSubmit: async (values) => {
      setLoader(true);

      const payload = {
        name: values.name,
        profile_image: values.profile_image,
        position: values.position,
      };

      try {
        const resp = MemberId
          ? await REQUEST_UPDATE_MEMBERS_COMMON(MemberId, payload)
          : await REQUEST_ADD_MEMBERS_COMMON(payload);

        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(resp.message);
          navigate("/members");
        } else {
          toast.error(resp.message);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || error.message);
      } finally {
        setLoader(false);
      }
    },
  });

  const handleFileChange = async (event: any, imageType: string) => {
    const fileInput = event.target;
    const file = fileInput?.files[0];
    const maxSizeInBytes = 1024 * 1024;

    if (file && file.size > maxSizeInBytes) {
      toast.error(IMAGE_ONE_MB_TOAST);
      fileInput.value = "";
      return;
    }

    const allowedImageTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (file && !allowedImageTypes.includes(file.type)) {
      toast.error(ONLY_IMAGES_ALLOWED_TOAST);
      fileInput.value = "";
      return;
    }

    if (file) {
      setFullScreenLoader(true);

      const formData = new FormData();
      formData.append("files", file);

      try {
        const resp: any = await REQUEST_FILE_UPLOAD_COMMON(formData);
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          formik.setFieldValue(imageType, String(resp?.data[0]?.file));
        } else {
          toast.error(resp.message);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || error.message);
      } finally {
        setFullScreenLoader(false);
      }
    }
  };

  const handleViewProject = async (id: any) => {
    setFullScreenLoader(true);
    try {
      const resp: any = await REQUEST_VIEW_MEMBERS_COMMON(id);
      if (checkStatusCodeSuccess(resp?.statusCode)) {
        setviewMemberData(resp?.data);
        toast.success(resp.message);
      } else {
        toast.error(resp.message);
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setFullScreenLoader(false);
    }
  };

  useEffect(() => {
    if (id) {
      handleViewProject(id);
    }
  }, [id]);

  document.title = MEMBERS_TITLE;

  const positionOptions = Object.entries(MembersPosition).map(
    ([key, value]) => ({
      value,
      label: value,
    })
  );

  return (
    <div className="top-div">
      <div className="container-fluid">
        <Row className="align-items-center mt-2 mb-1">
          <Col md={6} sm={12}>
            <span className="fs-4 mx-2">{id ? EDIT_MEMBER : ADD_MEMBER}</span>
          </Col>
          <Col md={6}>
            <BaseButton
              type="button"
              onClick={() => navigate("/members")}
              text={
                <>
                  <Icon path={mdiArrowLeft} size={0.7} className="me-2" />
                  {BACK}
                </>
              }
              color="info"
              className="mt-0 w-auto float-end"
            />
          </Col>
        </Row>

        <Card>
          <CardBody className="p-4">
            <form
              onSubmit={formik.handleSubmit}
              className="d-flex w-100 flex-column"
            >
              <div className="mb-2">
                <Row>
                  <Col xs={12} sm={6} md={4} className="mb-1">
                    <BaseInput
                      required={true}
                      label={MEMBER_NAME}
                      name="name"
                      className="mb-3"
                      type="text"
                      placeholder={InputPlaceHolder(MEMBER_NAME)}
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={String(formik.errors.name || "")}
                      touched={Boolean(formik.touched.name)}
                    />
                  </Col>

                  <Col xs={12} sm={6} md={4} className="mb-1">
                    <BaseSelectDropdown
                      label={MEMBER_POSITION}
                      className="basic-select border-primary"
                      classNamePrefix="select"
                      isDisabled={false}
                      isLoading={false}
                      name="position"
                      options={positionOptions}
                      onChange={(selectedOption: any) => {
                        formik.setFieldValue(
                          "position",
                          selectedOption ? selectedOption.value : ""
                        );
                        formik.setFieldTouched("position", true);
                        formik.validateField("position");
                      }}
                      onBlur={handleBlur}
                      value={positionOptions.find(
                        (option) => option.value === formik.values.position
                      )}
                      touched
                      error={String(formik.errors.name ||"")}
                      isClearable
                      required
                    />

                    {formik.errors.position && formik.touched.position && (
                      <div className="invalid-feedback">
                        {formik.errors.position}
                      </div>
                    )}
                  </Col>

                  <Col xs={12} sm={6} md={4} className="mb-1">
                    <BaseInput
                      accept="image/*"
                      label={MEMBER_IMAGE}
                      required={true}
                      name="profile_image"
                      tooltip={true}
                      tooltipIcon="help-circle-outline"
                      tooltipIconColor="info"
                      tooltipText="Only JPEG/PNG Allowed"
                      type="file"
                      onChange={(event) => {
                        handleFileChange(event, "profile_image");
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.errors.profile_image}
                      touched={formik.touched.profile_image}
                    />
                    {formik.values.profile_image && (
                      <span className="fw-normal mb-2 d-flex">
                        <Link
                          target="_blank"
                          className="text-link font-weight-bold"
                          to={`${BaseImageURL}${formik.values.profile_image}`}
                        >
                          View File
                        </Link>
                      </span>
                    )}
                  </Col>
                </Row>
              </div>

              <div className="d-flex justify-content-end">
                <BaseButton
                  type="submit"
                  text={
                    id
                      ? loader
                        ? UPDATING_LABEL
                        : UPDATE_LABEL
                      : loader
                      ? SUBMITTING_LABEL
                      : SUBMIT_LABEL
                  }
                  color="success"
                  className="mt-0 w-auto px-4 py-2"
                  size="sm"
                  loading={loader}
                  disabled={!formik.dirty || formik.isSubmitting}
                />
              </div>
            </form>
          </CardBody>
        </Card>
        {fullScreenLoader && (
          <div className="full-screen-loader">
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
};

export default MembersPage;
